import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Upload, Film, Loader2, Sparkles, Plus, X, Download, Trash2, AlertCircle, Check,
} from 'lucide-react';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import AnchorFrameIterator from '../components/AnchorFrameIterator';

const API = window.__API_BASE + '/api';
const MAX_BYTES = 32 * 1024 * 1024;

// Premium-only — match the backend gate.
const PREMIUM_EMAILS = ['rohit@eipimedia.com', 'nikhil@eipimedia.com'];

const ASPECT_RATIOS = [
  { id: '16:9',  label: '16:9 — Landscape' },
  { id: '9:16',  label: '9:16 — Vertical'  },
  { id: '1:1',   label: '1:1 — Square'     },
];

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function RunwayAlephRecipe() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPremium = !!user && PREMIUM_EMAILS.includes((user.email || '').toLowerCase());

  // Session state
  const [runId, setRunId] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceMeta, setSourceMeta] = useState(null); // { filename, bytes }
  const [iterations, setIterations] = useState([]); // newest first
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Generate state
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState('16:9');
  const [generating, setGenerating] = useState(false);

  // Reference images (Runway Aleph caps at 3) — uploaded immediately so the
  // operator can iterate across multiple prompts without re-uploading.
  const [refs, setRefs] = useState([]); // [{ url, filename, bytes, preview }]
  const [uploadingRef, setUploadingRef] = useState(false);
  const refInputRef = useRef(null);

  // Anchor frame (2026-02 new feature). Auto-extracted on upload-video.
  // ``anchorR0Url`` = the raw first frame PNG; ``anchorIterations`` = the
  // gpt-image-2 edits the operator stacks on it; ``selectedAnchorUrl`` is
  // sent to /generate as the primary identity anchor.
  const [anchorR0Url, setAnchorR0Url] = useState(null);
  const [anchorIterations, setAnchorIterations] = useState([]);
  const [selectedAnchorUrl, setSelectedAnchorUrl] = useState(null);

  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // ── Add a reference image ─────────────────────────────────────────────────
  const handleRefFile = async (file) => {
    if (!file || !runId) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image (PNG / JPG / WEBP).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Reference image too large (8 MB max).');
      return;
    }
    setUploadingRef(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const { data } = await axios.post(
        `${API}/recipes/runway-aleph/upload-ref-image?run_id=${runId}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const preview = URL.createObjectURL(file);
      setRefs((cur) => [...cur, { url: data.url, filename: data.filename, bytes: data.bytes, preview }].slice(0, 3));
      toast.success('Reference added.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reference upload failed');
    } finally {
      setUploadingRef(false);
    }
  };

  const handleRefInput = (e) => {
    const files = Array.from(e.target.files || []);
    const room = 3 - refs.length;
    files.slice(0, room).forEach(handleRefFile);
    if (e.target) e.target.value = '';
  };

  const removeRef = (idx) => {
    setRefs((cur) => {
      const next = [...cur];
      const [removed] = next.splice(idx, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file (MP4 / MOV / WEBM).');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Video too large (${fmtBytes(file.size)}). Max 32 MB.`);
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('video', file);
      const { data } = await axios.post(`${API}/recipes/runway-aleph/upload-video`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRunId(data.run_id);
      setSourceUrl(data.source_video_url);
      setSourceMeta({ filename: file.name, bytes: file.size });
      setIterations([]);
      // Anchor frame is auto-extracted by the backend on upload. If the
      // ffmpeg call failed (rare), ``anchor_frame_url`` will be null —
      // operator can still proceed with operator-uploaded refs only.
      if (data.anchor_frame_url) {
        setAnchorR0Url(data.anchor_frame_url);
        setSelectedAnchorUrl(data.anchor_frame_url);
      } else {
        setAnchorR0Url(null);
        setSelectedAnchorUrl(null);
      }
      setAnchorIterations([]);
      toast.success('Source video uploaded. Add a prompt and edit it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    if (e.target) e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // ── Reset session ─────────────────────────────────────────────────────────
  const resetSession = async () => {
    if (runId) {
      try { await axios.delete(`${API}/recipes/runway-aleph/runs/${runId}`); } catch { /* */ }
    }
    setRunId(null);
    setSourceUrl('');
    setSourceMeta(null);
    setIterations([]);
    setPrompt('');
    refs.forEach(r => r.preview && URL.revokeObjectURL(r.preview));
    setRefs([]);
    setAnchorR0Url(null);
    setAnchorIterations([]);
    setSelectedAnchorUrl(null);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // ── Generate an edit ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!runId || !sourceUrl) {
      toast.error('Upload a source video first.');
      return;
    }
    if (!prompt.trim()) {
      toast.error('Enter an edit prompt.');
      return;
    }

    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/recipes/runway-aleph/generate`, {
        run_id: runId,
        prompt: prompt.trim(),
        aspect_ratio: aspect,
        reference_image_urls: refs.map(r => r.url),
        anchor_frame_url: selectedAnchorUrl || undefined,
      });
      // Optimistically prepend the in-flight iteration to the stack
      setIterations((cur) => [{
        iteration_id: data.iteration_id,
        task_id: data.task_id,
        prompt: prompt.trim(),
        status: 'processing',
        video_url: null,
        created_at: new Date().toISOString(),
      }, ...cur]);
      setPrompt('');
      toast.info('Runway Aleph is editing your video…');
      startPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
      setGenerating(false);
    }
  };

  // ── Poll session for iteration updates ────────────────────────────────────
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      if (!runId) return;
      try {
        const { data } = await axios.get(`${API}/recipes/runway-aleph/runs/${runId}`);
        // Server returns iterations newest-first already ($position: 0)
        setIterations(data.iterations || []);
        const anyProcessing = (data.iterations || []).some(it => it.status === 'processing');
        if (!anyProcessing) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setGenerating(false);
          const latest = data.iterations?.[0];
          if (latest?.status === 'completed') toast.success('Edit ready.');
          else if (latest?.status === 'failed') toast.error(latest.error || 'Edit failed');
        }
      } catch { /* keep polling */ }
    }, 6000);
  }, [runId]);

  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // ── Premium gate ──────────────────────────────────────────────────────────
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#0a1018] text-white flex items-center justify-center p-6" data-testid="aleph-premium-gate">
        <div className="max-w-md w-full rounded-2xl border border-white/10 p-6 text-center" style={{ background: 'rgba(167,139,250,0.06)' }}>
          <Film className="w-10 h-10 mx-auto mb-3 text-violet-400" />
          <h1 className="text-lg font-bold mb-1">Runway Aleph — Premium</h1>
          <p className="text-sm text-white/60 mb-4">
            Video-to-video editing is a premium recipe. Contact support to enable it on your account.
          </p>
          <button
            onClick={() => navigate('/recipes')}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="aleph-back-recipes-btn"
          >
            Back to Recipes
          </button>
        </div>
      </div>
    );
  }

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen text-white" style={{ background: '#0a1018' }} data-testid="runway-aleph-recipe">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-3 flex items-center justify-between sticky top-0 z-10" style={{ background: 'rgba(10,16,24,0.92)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/recipes')}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="aleph-back-btn"
          >
            <ArrowLeft className="w-4 h-4 text-white/70" />
          </button>
          <Film className="w-5 h-5 text-violet-400" />
          <div>
            <h1 className="text-sm font-bold tracking-tight">Runway Aleph</h1>
            <p className="text-[10px] text-white/40">Video-to-video editing · Gen-4 Aleph</p>
          </div>
        </div>
        {runId && (
          <button
            onClick={resetSession}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all"
            data-testid="aleph-new-session-btn"
          >
            <Trash2 className="w-3 h-3" /> New Session
          </button>
        )}
      </header>

      {/* Two-column workspace */}
      <main className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">

        {/* LEFT: Upload + Stack ------------------------------------------- */}
        <section className="space-y-4">
          {!sourceUrl ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                dragOver ? 'border-violet-400 bg-violet-500/10' : 'border-white/15 hover:border-violet-500/50 hover:bg-white/3'
              }`}
              style={{ minHeight: '60vh', background: dragOver ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.02)' }}
              data-testid="aleph-upload-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
                className="hidden"
                onChange={handleFileInput}
                data-testid="aleph-file-input"
              />
              {uploading ? (
                <>
                  <Loader2 className="w-10 h-10 text-violet-400 animate-spin mb-3" />
                  <span className="text-sm text-white/60">Uploading to S3…</span>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(167,139,250,0.15)', border: '2px solid rgba(167,139,250,0.3)' }}>
                    <Plus className="w-8 h-8 text-violet-300" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-base font-semibold mb-1">Drop your video here</h3>
                  <p className="text-xs text-white/40 mb-4">or click to browse · MP4 / MOV / WEBM · max 32 MB</p>
                  <p className="text-[10px] text-white/30 max-w-sm text-center leading-relaxed">
                    Runway Aleph will use the first 5 seconds. Cast, composition, and timing are preserved — only the edit you describe changes.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Source video header */}
              <div className="rounded-2xl border border-violet-500/30 overflow-hidden" style={{ background: 'rgba(167,139,250,0.04)' }} data-testid="aleph-source-card">
                <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">Source</span>
                    <span className="text-[11px] text-white/60 truncate max-w-[300px]">{sourceMeta?.filename || 'source.mp4'}</span>
                  </div>
                  {sourceMeta?.bytes && (
                    <span className="text-[10px] text-white/30">{fmtBytes(sourceMeta.bytes)}</span>
                  )}
                </div>
                <video
                  src={sourceUrl}
                  controls
                  preload="metadata"
                  className="w-full bg-black"
                  style={{ maxHeight: '50vh', objectFit: 'contain' }}
                  data-testid="aleph-source-video"
                />
              </div>

              {/* Anchor Frame Iterator — 2026-02 new feature.
                  First frame of the uploaded video, auto-extracted and
                  paintable with gpt-image-2. The selected card becomes
                  the dominant identity anchor sent to Runway Aleph on
                  /generate. */}
              {(anchorR0Url || anchorIterations.length > 0) && (
                <div className="rounded-2xl border border-violet-500/20 p-4" style={{ background: 'rgba(167,139,250,0.05)' }}>
                  <AnchorFrameIterator
                    mode="recipe"
                    runId={runId}
                    r0Url={anchorR0Url}
                    iterations={anchorIterations}
                    selectedUrl={selectedAnchorUrl}
                    onSelect={setSelectedAnchorUrl}
                    onIterationsChanged={setAnchorIterations}
                    disabled={generating || uploading}
                  />
                </div>
              )}

              {/* Iteration stack — newest at top, ABOVE the source visually */}
              {iterations.length > 0 && (
                <div className="space-y-3" data-testid="aleph-iterations-stack">
                  {iterations.map((it, idx) => (
                    <IterationCard
                      key={it.iteration_id}
                      iteration={it}
                      roundLabel={`R${iterations.length - idx}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* RIGHT: Prompt + Generate -------------------------------------- */}
        <section className="space-y-4" data-testid="aleph-controls">
          <div className="rounded-2xl border border-white/10 p-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
            {/* Reference images (up to 3) */}
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2">
              References <span className="text-white/25 normal-case font-normal">· optional · max 3</span>
            </label>
            <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="aleph-refs-grid">
              {[0, 1, 2].map((slot) => {
                const r = refs[slot];
                if (r) {
                  return (
                    <div key={slot} className="relative rounded-md overflow-hidden border border-violet-500/30 aspect-square group">
                      <img src={r.preview} alt={`ref ${slot + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeRef(slot)}
                        disabled={generating}
                        className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70 hover:bg-red-500/30 transition-colors disabled:opacity-30"
                        data-testid={`aleph-ref-remove-${slot}`}
                        title="Remove reference"
                      >
                        <X className="w-2.5 h-2.5 text-white/80" />
                      </button>
                      <span className="absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded text-[7px] uppercase font-bold tracking-wide bg-white/10 text-white/70">
                        Ref {slot + 1}
                      </span>
                    </div>
                  );
                }
                const canAdd = !!runId && !uploadingRef && !generating && refs.length === slot;
                return (
                  <button
                    key={slot}
                    onClick={() => canAdd && refInputRef.current?.click()}
                    disabled={!canAdd}
                    title={!runId ? 'Upload a source video first' : `Add reference ${slot + 1}`}
                    className="rounded-md border border-dashed border-white/15 text-white/40 hover:border-violet-500/40 hover:text-violet-400 transition-all flex flex-col items-center justify-center gap-0.5 aspect-square disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-white/40"
                    data-testid={`aleph-ref-add-${slot}`}
                  >
                    {uploadingRef && refs.length === slot ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        <span className="text-[8px]">add</span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            <input
              ref={refInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={handleRefInput}
              data-testid="aleph-ref-input"
            />

            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2">
              Edit Prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={sourceUrl
                ? "e.g. make it golden hour, warm cinematic light · change costume to red · add light rain"
                : "Upload a source video first to enable prompting…"}
              disabled={!sourceUrl || generating}
              rows={6}
              maxLength={500}
              className="w-full text-sm px-3 py-2.5 rounded-xl border border-white/10 bg-white/3 text-white placeholder:text-white/25 outline-none focus:border-violet-500/50 transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="aleph-prompt-input"
            />
            <div className="mt-1 flex justify-between text-[9px] text-white/30">
              <span>{prompt.length}/500</span>
              <span>Aleph uses first 5s of the source</span>
            </div>

            <div className="mt-3">
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-1.5">
                Aspect Ratio
              </label>
              <div className="grid grid-cols-3 gap-1.5" data-testid="aleph-aspect-selector">
                {ASPECT_RATIOS.map(r => (
                  <button
                    key={r.id}
                    onClick={() => setAspect(r.id)}
                    disabled={generating}
                    className={`py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${
                      aspect === r.id
                        ? 'border-violet-500/60 bg-violet-500/15 text-violet-200'
                        : 'border-white/10 bg-white/3 text-white/40 hover:bg-white/10'
                    }`}
                    data-testid={`aleph-aspect-${r.id.replace(':','-')}`}
                  >
                    {r.id}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={!sourceUrl || !prompt.trim() || generating}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                background: generating
                  ? 'rgba(167,139,250,0.15)'
                  : 'linear-gradient(135deg, #A78BFA, #7C3AED)',
                color: 'white',
              }}
              data-testid="aleph-generate-btn"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {generating ? 'Editing…' : 'Edit with Aleph'}
            </button>

            <p className="mt-2 text-[9px] text-white/30 leading-snug">
              Each edit costs ≈ 75 credits (~$0.75 · 15 c/s × 5s).
              Edits stack atop the source — your original is never overwritten.
            </p>
          </div>

          {/* Tips card */}
          <div className="rounded-2xl border border-white/10 p-4" style={{ background: 'rgba(255,255,255,0.015)' }}>
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2">Prompt Tips</h4>
            <ul className="space-y-1.5 text-[10px] text-white/50 leading-relaxed">
              <li>• Start with a verb: <span className="text-violet-300">"make"</span>, <span className="text-violet-300">"change"</span>, <span className="text-violet-300">"add"</span></li>
              <li>• Be specific about <span className="text-white/70">what</span> changes, leave <span className="text-white/70">what stays</span> implicit</li>
              <li>• Lighting / time-of-day shifts work especially well</li>
              <li>• Wardrobe / prop swaps are reliable</li>
              <li>• Identity, framing, and motion are preserved automatically</li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}


// ─── Iteration card — small, self-contained ──────────────────────────────────
function IterationCard({ iteration, roundLabel }) {
  const isProcessing = iteration.status === 'processing';
  const isFailed = iteration.status === 'failed';
  const isDone = iteration.status === 'completed';

  const downloadVideo = async () => {
    if (!iteration.video_url) return;
    try {
      const r = await fetch(`${API}/recipes/runway-aleph/download-video?url=${encodeURIComponent(iteration.video_url)}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `aleph-${roundLabel}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      window.open(iteration.video_url, '_blank');
    }
  };

  return (
    <div className="rounded-2xl border overflow-hidden transition-all" style={{
      background: isDone ? 'rgba(167,139,250,0.04)' : 'rgba(255,255,255,0.02)',
      borderColor: isDone ? 'rgba(167,139,250,0.4)' : isFailed ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)',
    }} data-testid={`aleph-iteration-${roundLabel}`}>
      <div className="px-4 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">
          Aleph · {roundLabel}
        </span>
        {isProcessing && (
          <span className="flex items-center gap-1 text-[10px] text-violet-300">
            <Loader2 className="w-3 h-3 animate-spin" /> Editing…
          </span>
        )}
        {isDone && (
          <span className="flex items-center gap-1 text-[10px] text-emerald-300">
            <Check className="w-3 h-3" /> Done
          </span>
        )}
        {isFailed && (
          <span className="flex items-center gap-1 text-[10px] text-red-300">
            <AlertCircle className="w-3 h-3" /> Failed
          </span>
        )}
        <span className="ml-auto text-[10px] text-white/40 truncate max-w-[55%] italic">
          "{iteration.prompt}"
        </span>
        {isDone && iteration.video_url && (
          <button
            onClick={downloadVideo}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Download"
            data-testid={`aleph-download-${roundLabel}`}
          >
            <Download className="w-3 h-3 text-white/60" />
          </button>
        )}
      </div>

      {isProcessing && (
        <div className="bg-black flex items-center justify-center" style={{ minHeight: '180px' }}>
          <div className="text-center">
            <Loader2 className="w-6 h-6 text-violet-400 animate-spin mx-auto mb-2" />
            <p className="text-[10px] text-white/40">Runway is processing… ~2 min</p>
          </div>
        </div>
      )}

      {isFailed && (
        <div className="p-4">
          <p className="text-[11px] text-red-300/90 leading-relaxed">
            {iteration.error || 'Generation failed'}
          </p>
        </div>
      )}

      {isDone && iteration.video_url && (
        <video
          src={iteration.video_url}
          controls
          preload="metadata"
          className="w-full bg-black"
          style={{ maxHeight: '50vh', objectFit: 'contain' }}
          data-testid={`aleph-iteration-video-${roundLabel}`}
        />
      )}
    </div>
  );
}
